import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import useSWR, { mutate } from 'swr';
import { Permissions } from '../../config';
import { Add, AddAlert, LocalOffer } from '@mui/icons-material';
import { Skeleton, Table, TableHead, TableBody, TableRow, TableCell, IconButton, Tooltip } from '@mui/material';
import { Edit, Delete } from '@mui/icons-material';

import GridContainer from 'components/Grid/GridContainer.js';
import GridItem from 'components/Grid/GridItem.js';
import Card from 'components/Card/Card.js';
import CardBody from 'components/Card/CardBody.js';
import SparaTitle from 'components/SparaTitle/SparaTitle';
import SparaButton from 'components/SparaButton/SparaButton';
import SparaFilter from 'components/SparaFilter/SparaFilter';
import SnackbarCustom from 'components/Snackbar/Snackbar.js';
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { fetcher, offerCategoryApi } from '../../lib/api';
import AddCategory from './addCategory';
import styles from './category.module.scss';

const SKELETON_COUNT = 8;

const RowSkeleton = () => (
    <TableRow>
        <TableCell><Skeleton variant="rounded" width={36} height={36} /></TableCell>
        <TableCell>
            <Skeleton variant="text" width={130} height={18} />
            <Skeleton variant="text" width={100} height={14} sx={{ mt: 0.3 }} />
        </TableCell>
        <TableCell><Skeleton variant="rounded" width={80} height={22} sx={{ borderRadius: 20 }} /></TableCell>
        <TableCell><Skeleton variant="rounded" width={40} height={22} sx={{ borderRadius: 20 }} /></TableCell>
        <TableCell align="right">
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="circular" width={28} height={28} />
            </div>
        </TableCell>
    </TableRow>
);

// Build a flat list of rows: parent categories (parent_id === 0) followed by their children
const flattenCategories = (data) => {
    if (!data || data.length === 0) return [];

    const parents = data
        .filter((c) => c.parent_id === 0)
        .sort((a, b) => a.sort_order - b.sort_order);

    const rows = [];
    parents.forEach((parent) => {
        rows.push({ ...parent, _level: 'parent' });
        const children = data
            .filter((c) => c.parent_id === parent.id)
            .sort((a, b) => a.sort_order - b.sort_order);
        children.forEach((child) => {
            rows.push({ ...child, _level: 'child', _parentName: parent.name });
        });
    });
    return rows;
};

const OfferCategories = () => {
    const { t } = useTranslation();
    const [alert, setAlert] = useState(null);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, category: null });
    const [search, setSearch] = useState('');
    const [notification, setNotification] = useState({ open: false, message: '', type: 'success' });

    const userPermission = JSON.parse(localStorage.getItem('user')).permission;
    const canCreate = userPermission.includes(Permissions.CATEGORY_CREATE);
    const canUpdate = userPermission.includes(Permissions.CATEGORY_UPDATE);
    const canDelete = userPermission.includes(Permissions.CATEGORY_DELETE);

    const { data, isLoading } = useSWR(offerCategoryApi.list, fetcher);

    const allRows = useMemo(() => flattenCategories(data?.results || []), [data]);

    const topLevelCategories = useMemo(
        () => allRows.filter((r) => r._level === 'parent'),
        [allRows]
    );

    const filtered = useMemo(() => {
        if (!search.trim()) return allRows;
        const q = search.toLowerCase();
        return allRows.filter(
            (r) => r.name?.toLowerCase().includes(q) || r.name_en?.toLowerCase().includes(q)
        );
    }, [allRows, search]);

    const showNotification = (message, type = 'success') => {
        setNotification({ open: true, message, type });
        setTimeout(() => setNotification((prev) => ({ ...prev, open: false })), 4000);
    };

    const handleSubmit = async (formData, isUpdate) => {
        try {
            await fetcher(
                isUpdate ? offerCategoryApi.update(formData.id) : offerCategoryApi.create,
                isUpdate ? 'PUT' : 'POST',
                formData
            );
            mutate(offerCategoryApi.list);
            showNotification(t('common.saved_successfully'), 'success');
        } catch {
            showNotification(t('common.error_occurred'), 'warning');
        }
    };

    const handleDelete = (category) => {
        setDeleteDialog({ open: true, category });
    };

    const confirmDelete = async () => {
        const { category } = deleteDialog;
        setDeleteDialog({ open: false, category: null });
        try {
            await fetcher(offerCategoryApi.delete(category.id), 'DELETE');
            mutate(offerCategoryApi.list);
            showNotification(t('common.deleted_successfully'), 'success');
        } catch {
            showNotification(t('common.error_occurred'), 'warning');
        }
    };

    const openForm = (data = {}) => {
        setAlert(
            <AddCategory
                classes={{}}
                categories={topLevelCategories}
                data={data}
                onCancel={() => setAlert(null)}
                onSubmit={(formData, isUpdate) => {
                    handleSubmit(formData, isUpdate);
                    setAlert(null);
                }}
            />
        );
    };

    const totalCount = allRows.length;
    const parentCount = topLevelCategories.length;

    return (
        <GridContainer>
            {alert && createPortal(alert, document.body)}
            <ConfirmDialog
                open={deleteDialog.open}
                type="danger"
                title={t('are_you_sure')}
                message={t('delete_confirmation')}
                confirmText={t('delete_yes')}
                cancelText={t('cancel')}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteDialog({ open: false, category: null })}
            />
            <SnackbarCustom
                place="br"
                icon={AddAlert}
                color={notification.type === 'success' ? 'success' : 'warning'}
                message={notification.message}
                open={notification.open}
                closeNotification={() => setNotification((prev) => ({ ...prev, open: false }))}
            />
            <GridItem xs={12}>
                <SparaTitle
                    title={t('menu.categories')}
                    rightComponent={
                        canCreate && (
                            <SparaButton color="primary" icon={Add} onClick={() => openForm()}>
                                {t('category.add_new')}
                            </SparaButton>
                        )
                    }
                />
                <Card style={{ margin: '0px' }}>
                    <CardBody>
                        <SparaFilter
                            filters={{}}
                            filterGroups={[]}
                            onFilterClick={() => {}}
                            onClearAll={() => setSearch('')}
                            search={search}
                            onSearchChange={(e) => setSearch(e?.target?.value ?? e)}
                        />

                        <div className={styles.listHeader}>
                            {!isLoading && (
                                <span className={styles.listHeaderCount}>
                                    {search
                                        ? `${filtered.length} ${t('menu.categories').toLowerCase()}`
                                        : `${totalCount} ${t('menu.categories').toLowerCase()} — ${parentCount} ${t('parent_category').toLowerCase()}`
                                    }
                                </span>
                            )}
                        </div>

                        <div className={styles.tableWrap}>
                            <Table size="small" sx={{ width: '100%' }}>
                                <TableHead>
                                    <TableRow className={styles.tableHead}>
                                        <TableCell>{t('icon')}</TableCell>
                                        <TableCell>{t('name')}</TableCell>
                                        <TableCell>{t('parent_id')}</TableCell>
                                        <TableCell>{t('order')}</TableCell>
                                        <TableCell align="right">{t('actions')}</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isLoading
                                        ? Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                                            <RowSkeleton key={i} />
                                        ))
                                        : filtered.map((cat) => (
                                            <TableRow
                                                key={cat.id}
                                                className={`${styles.tableRow} ${cat._level === 'parent' ? styles.parentRow : styles.childRow}`}
                                            >
                                                <TableCell>
                                                    {(cat.icon || cat.logo)
                                                        ? <img src={cat.icon || cat.logo} alt={cat.name} className={styles.categoryIcon} />
                                                        : <div className={styles.iconPlaceholder}><LocalOffer fontSize="small" /></div>
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <p className={styles.categoryName}>{cat.name}</p>
                                                    <p className={styles.categoryNameEn}>{cat.name_en}</p>
                                                </TableCell>
                                                <TableCell>
                                                    {cat._level === 'parent'
                                                        ? <span className={styles.parentBadge}>{t('parent_category')}</span>
                                                        : <span style={{ fontSize: '0.8rem', color: '#718096' }}>{cat._parentName}</span>
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <span className={styles.sortBadge}>{cat.sort_order ?? '—'}</span>
                                                </TableCell>
                                                <TableCell align="right">
                                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                                        {canUpdate && (
                                                            <Tooltip title={t('edit')}>
                                                                <IconButton size="small" onClick={() => openForm(cat)}>
                                                                    <Edit sx={{ fontSize: 17 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                        {canDelete && (
                                                            <Tooltip title={t('delete')}>
                                                                <IconButton size="small" color="error" onClick={() => handleDelete(cat)}>
                                                                    <Delete sx={{ fontSize: 17 }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    }
                                </TableBody>
                            </Table>
                        </div>
                    </CardBody>
                </Card>
            </GridItem>
        </GridContainer>
    );
};

export default OfferCategories;
